import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  FiBriefcase,
  FiCheckCircle,
  FiHome,
  FiImage,
  FiMapPin,
  FiMessageCircle,
  FiPhone,
  FiUploadCloud,
} from 'react-icons/fi';

import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/useToast';

import {
  AvatarPreview,
  AvatarUploader,
  FieldGroup,
  FormField,
  GhostButton,
  ModalForm,
  PrimaryButton,
  SectionLabel,
  UploadDropzone,
} from './styles';

interface IPartnerCategory {
  id: string;
  title: string;
}

interface IState {
  uf: string;
  nome: string;
}

interface ICity {
  id: number;
  nome: string;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}

interface IFormValues {
  name: string;
  address: string;
  address2: string;
  postal_code: string;
  city_id: string | number;
  state: string;
  whatsapp: string;
  phone: string;
  email: string;
  avatar: File | string;
  description: string;
  category_id: string;
  syndicate_id: string;
}

interface IProps {
  showModal: boolean;
  handleShowModal: (value: boolean) => void;
  handlePartners: (partners: []) => void;
  handleLoading: (value: boolean) => void;
}

const cepMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/(\d{5})(\d)/, '$1-$2');
};

const phoneMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Nome é obrigatório'),
  address: Yup.string().required('Endereço é obrigatório'),
  address2: Yup.string().required('Bairro/complemento é obrigatório'),
  postal_code: Yup.string().required('CEP é obrigatório'),
  city_id: Yup.mixed().required('Cidade é obrigatória'),
  state: Yup.string().required('Estado é obrigatório'),
  whatsapp: Yup.string().nullable(),
  phone: Yup.string().nullable(),
  email: Yup.string().email('E-mail inválido').nullable(),
  description: Yup.string().nullable(),
  category_id: Yup.string().required('Categoria é obrigatória'),
  syndicate_id: Yup.string().required('Sindicato é obrigatório'),
});

const AddPartnerModal: React.FC<IProps> = ({
  showModal,
  handleShowModal,
  handlePartners,
  handleLoading,
}) => {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [partnersCategories, setPartnersCategories] = useState<
    IPartnerCategory[]
  >([]);
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [unions, setUnions] = useState<ISyndicate[]>([]);

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    handleShowModal(false);
    setCities([]);
    setAvatarFile(null);
    setAvatarPreview('');
  }, [handleShowModal]);

  const handleLoadCities = useCallback(async (uf: string) => {
    if (!uf) {
      setCities([]);
      return;
    }
    const response = await api.get<ICity[]>(`/ibge/cities/${uf}`);
    setCities(response.data);
  }, []);

  const handleAvatarUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    },
    [],
  );

  const handleCreatePartner = useCallback(
    async (values: IFormValues) => {
      const { avatar, ...rest } = values;
      const partnerData = new FormData();
      if (avatar) partnerData.append('avatar', avatar as Blob);
      partnerData.append('document', JSON.stringify(rest));

      try {
        setSubmitting(true);
        handleLoading(true);

        await api.post('/unions-partners', partnerData);
        const updatedList = await api.get('/unions-partners');
        handlePartners(updatedList.data);

        addToast({
          title: 'Sucesso',
          body: 'Novo convênio adicionado.',
        });
        handleClose();
      } catch {
        addToast({
          title: 'Erro',
          body: 'Falha ao adicionar o convênio.',
        });
      } finally {
        setSubmitting(false);
        handleLoading(false);
      }
    },
    [addToast, handleLoading, handleClose, handlePartners],
  );

  useEffect(() => {
    if (!showModal) return;
    const loadData = async () => {
      const [categoriesRes, statesRes] = await Promise.all([
        api.get<IPartnerCategory[]>('/unions-partners/categories'),
        api.get<IState[]>('/ibge/states'),
      ]);
      setPartnersCategories(categoriesRes.data);
      setStates(statesRes.data);

      if (user.role === 'admin') {
        const unionsRes = await api.get<ISyndicate[]>('/unions');
        setUnions(unionsRes.data);
      }
    };
    loadData();
  }, [showModal, user.role]);

  return (
    <Modal show={showModal} onHide={handleClose} centered size="lg">
      <Formik<IFormValues>
        initialValues={{
          name: '',
          address: '',
          address2: '',
          postal_code: '',
          city_id: '',
          state: '',
          whatsapp: '',
          phone: '',
          email: '',
          avatar: '',
          description: '',
          category_id: '',
          syndicate_id: user.role === 'admin' ? '' : user.syndicate_id || '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleCreatePartner}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <>
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                Adicionar convênio
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <ModalForm onSubmit={handleSubmit}>
                <SectionLabel>
                  <FiBriefcase size={11} />
                  Identificação
                </SectionLabel>

                <FieldGroup $cols={3}>
                  <FormField $span={2}>
                    <label htmlFor="name">
                      Nome *
                      <input
                        id="name"
                        name="name"
                        placeholder="Digite o nome do convênio"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.name && errors.name && (
                      <span className="error">{errors.name}</span>
                    )}
                  </FormField>
                  <FormField>
                    <label htmlFor="category_id">
                      Categoria *
                      <select
                        id="category_id"
                        name="category_id"
                        value={values.category_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        <option value="">Selecione</option>
                        {partnersCategories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.title}
                          </option>
                        ))}
                      </select>
                    </label>
                    {touched.category_id && errors.category_id && (
                      <span className="error">{errors.category_id}</span>
                    )}
                  </FormField>
                </FieldGroup>

                <SectionLabel>
                  <FiMapPin size={11} />
                  Endereço
                </SectionLabel>

                <FieldGroup>
                  <FormField>
                    <label htmlFor="address">
                      Endereço *
                      <input
                        id="address"
                        name="address"
                        placeholder="Rua, número"
                        value={values.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.address && errors.address && (
                      <span className="error">{errors.address}</span>
                    )}
                  </FormField>
                  <FormField>
                    <label htmlFor="address2">
                      Bairro / Complemento *
                      <input
                        id="address2"
                        name="address2"
                        placeholder="Bairro, complemento"
                        value={values.address2}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.address2 && errors.address2 && (
                      <span className="error">{errors.address2}</span>
                    )}
                  </FormField>
                </FieldGroup>

                <FieldGroup $cols={3}>
                  <FormField>
                    <label htmlFor="state">
                      Estado *
                      <select
                        id="state"
                        name="state"
                        value={values.state}
                        onChange={e => {
                          setFieldValue('state', e.target.value);
                          setFieldValue('city_id', '');
                          handleLoadCities(e.target.value);
                        }}
                        onBlur={handleBlur}
                      >
                        <option value="">UF</option>
                        {states.map(state => (
                          <option value={state.uf} key={state.uf}>
                            {state.uf}
                          </option>
                        ))}
                      </select>
                    </label>
                    {touched.state && errors.state && (
                      <span className="error">{errors.state}</span>
                    )}
                  </FormField>
                  <FormField>
                    <label htmlFor="city_id">
                      Cidade *
                      <select
                        id="city_id"
                        name="city_id"
                        value={values.city_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!values.state}
                      >
                        <option value="">
                          {values.state
                            ? 'Selecione o município'
                            : 'Escolha o estado primeiro'}
                        </option>
                        {cities.map(city => (
                          <option value={city.id} key={city.id}>
                            {city.nome}
                          </option>
                        ))}
                      </select>
                    </label>
                    {touched.city_id && errors.city_id && (
                      <span className="error">{errors.city_id as string}</span>
                    )}
                  </FormField>
                  <FormField>
                    <label htmlFor="postal_code">
                      CEP *
                      <input
                        id="postal_code"
                        name="postal_code"
                        placeholder="00000-000"
                        maxLength={9}
                        value={values.postal_code}
                        onChange={e => setFieldValue('postal_code', cepMask(e.target.value))}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.postal_code && errors.postal_code && (
                      <span className="error">{errors.postal_code}</span>
                    )}
                  </FormField>
                </FieldGroup>

                <SectionLabel>
                  <FiPhone size={11} />
                  Contato
                </SectionLabel>

                <FieldGroup $cols={3}>
                  <FormField>
                    <label htmlFor="phone">
                      Telefone
                      <input
                        id="phone"
                        name="phone"
                        placeholder="(00) 0000-0000"
                        maxLength={15}
                        value={values.phone}
                        onChange={e => setFieldValue('phone', phoneMask(e.target.value))}
                        onBlur={handleBlur}
                      />
                    </label>
                  </FormField>
                  <FormField>
                    <label htmlFor="whatsapp">
                      WhatsApp
                      <input
                        id="whatsapp"
                        name="whatsapp"
                        placeholder="(00) 00000-0000"
                        maxLength={15}
                        value={values.whatsapp}
                        onChange={e => setFieldValue('whatsapp', phoneMask(e.target.value))}
                        onBlur={handleBlur}
                      />
                    </label>
                  </FormField>
                  <FormField>
                    <label htmlFor="email">
                      E-mail
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="contato@convenio.com"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                    {touched.email && errors.email && (
                      <span className="error">{errors.email}</span>
                    )}
                  </FormField>
                </FieldGroup>

                <SectionLabel>
                  <FiMessageCircle size={11} />
                  Descrição
                </SectionLabel>

                <FormField>
                  <label htmlFor="description">
                    Descrição do convênio
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      placeholder="Descreva os benefícios oferecidos pelo convênio..."
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </label>
                </FormField>

                <SectionLabel>
                  <FiImage size={11} />
                  Logotipo
                </SectionLabel>

                <AvatarUploader>
                  <AvatarPreview $hasImage={!!avatarPreview}>
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Logotipo" />
                    ) : (
                      <FiImage size={26} />
                    )}
                  </AvatarPreview>
                  <UploadDropzone $hasFile={!!avatarFile}>
                    <span className="icon">
                      <FiUploadCloud size={18} />
                    </span>
                    <div className="info">
                      <span className="title">
                        {avatarFile?.name || 'Selecionar logotipo'}
                      </span>
                      <span className="hint">
                        PNG, JPG ou JPEG · clique para escolher
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/jpg"
                      onChange={event => {
                        handleAvatarUpload(event);
                        const file = event.target.files?.[0];
                        if (file) setFieldValue('avatar', file);
                      }}
                    />
                  </UploadDropzone>
                </AvatarUploader>

                {user.role === 'admin' && (
                  <>
                    <SectionLabel>
                      <FiHome size={11} />
                      Vínculo
                    </SectionLabel>
                    <FormField>
                      <label htmlFor="syndicate_id">
                        Sindicato *
                        <select
                          id="syndicate_id"
                          name="syndicate_id"
                          value={values.syndicate_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="">Selecione um sindicato</option>
                          {unions.map(syndicate => (
                            <option value={syndicate.id} key={syndicate.id}>
                              {syndicate.nome_fantasia}
                            </option>
                          ))}
                        </select>
                      </label>
                      {touched.syndicate_id && errors.syndicate_id && (
                        <span className="error">{errors.syndicate_id}</span>
                      )}
                    </FormField>
                  </>
                )}
              </ModalForm>
            </Modal.Body>

            <Modal.Footer>
              <GhostButton type="button" onClick={handleClose}>
                Cancelar
              </GhostButton>
              <PrimaryButton
                type="button"
                onClick={() => handleSubmit()}
                disabled={submitting}
              >
                <FiCheckCircle size={14} />
                {submitting ? 'Salvando...' : 'Salvar convênio'}
              </PrimaryButton>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  );
};

export default AddPartnerModal;
